/* Copyright (C) nexleader - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written for nexleader <myipsat.com>, 2016-2026
 */

import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { BsModalService } from 'ngx-bootstrap/modal';
import { catchError, forkJoin, of, tap } from 'rxjs';

import { ArchetypeReportService, ArchetypeReport } from '../../resources/archetype-report.service';
import { UserService } from '../../../../services/user.service';
import { ErrorHandlerService } from '../../../../services/error-handler.service';
import { AuthService } from '../../../../services/auth.service';
import { SurveyResultService } from '../../../core/views/survey-result/survey-result.service';
import { RegenerateArchetypeReportModalComponent } from '../../components/modals/regenerate-archetype-report-modal/regenerate-archetype-report-modal.component';
import { ARCHETYPE_DISPLAY_NAMES, Archetype } from '../../engine/archetype-engine';
import { ArchetypeWheelComponent } from '../../components/archetype-wheel/archetype-wheel.component';
import { ArchetypeIconComponent } from '../../components/archetype-icon/archetype-icon.component';
import { ArchetypeDashboardCardComponent } from '../../components/dashboard-card/dashboard-card.component';
import { DASHBOARD_CARDS, DashboardCardId } from '../../data/dashboard-content';
import { DynamicOperatingPatternModalComponent } from '../../components/modals/dynamic-operating-pattern-modal/dynamic-operating-pattern-modal.component';
import { IntegratedInsightsModalComponent } from '../../components/modals/integrated-insights-modal/integrated-insights-modal.component';
import { DisclosureFeedbackLoopModalComponent } from '../../components/modals/disclosure-feedback-loop-modal/disclosure-feedback-loop-modal.component';
import { DevelopmentFocusModalComponent } from '../../components/modals/development-focus-modal/development-focus-modal.component';
import { ArchetypeInterfacesModalComponent } from '../../components/modals/archetype-interfaces-modal/archetype-interfaces-modal.component';
import { IdentityStatementsModalComponent } from '../../components/modals/identity-statements-modal/identity-statements-modal.component';
import { NexleaderShareableIpsatModalComponent } from '../../../core/components/modals/shareable-ipsat-modal/shareable-ipsat-modal.component';
import { NexleaderActionAnchorDirective } from '../../../../directives/action-anchor.directive';

// Archetype overview metadata — taglines, icons, design system panel colors,
// and expanded descriptions (shown when user clicks Learn more on the framework cards).
// Descriptions sourced verbatim from content-package/archetypes/*.faith.json.
// Full per-user report content is loaded from archetypeReport.content at runtime.
const ARCHETYPE_META: Record<Archetype, { tagline: string; icon: string; panelClass: string; description: string }> = {
  visionary_strategist: {
    tagline: 'Sees the big picture and turns ideas into structured, achievable plans.',
    icon: 'fa-compass', panelClass: 'hgreen',
    description: 'Visionary Strategists synthesize complexity into clarity and lead with insight, logic, and long-range vision. They naturally see patterns others miss and create frameworks that align people and resources toward meaningful results.',
  },
  bold_initiator: {
    tagline: 'Turns vision into motion through decisive action and catalytic energy.',
    icon: 'fa-bolt', panelClass: 'hred',
    description: 'Bold Initiators act decisively, embrace risk, and generate momentum when others hesitate. They bring courage, energy, and drive that mobilizes teams and launches new ventures. Their challenge is balancing speed with sustainability.',
  },
  encouraging_connector: {
    tagline: 'Builds trust and belonging through relational investment and empathy.',
    icon: 'fa-heart', panelClass: 'hyellow',
    description: 'Encouraging Connectors create relational momentum, draw people together, and motivate through empathy and shared purpose. They build bridges between people and foster environments where everyone feels valued and included.',
  },
  reflective_guide: {
    tagline: 'Brings wisdom through perspective, deep listening, and thoughtful questions.',
    icon: 'fa-book', panelClass: 'hviolet',
    description: 'Reflective Guides listen deeply, ask discerning questions, and help others gain clarity and meaning. They bring wisdom born of contemplation and help teams slow down to find the right path forward.',
  },
  steady_builder: {
    tagline: 'Creates stability and reliability that makes sustained progress possible.',
    icon: 'fa-cogs', panelClass: 'hblue',
    description: 'Steady Builders value process, stewardship, and consistent execution. They turn vision into dependable systems and ensure that progress is sustained over time. Their reliability creates the foundation others depend on.',
  },
  curious_explorer: {
    tagline: 'Discovers possibilities others overlook through learning and creative connection.',
    icon: 'fa-search', panelClass: 'hyellow',
    description: 'Curious Explorers pursue learning, innovation, and creative connections. They expand what\'s possible by asking "what if?" and connecting dots across domains. Their curiosity opens doors to new opportunities.',
  },
};

@Component({
  selector: 'app-archetype-report-view',
  standalone: true,
  imports: [
    NexleaderActionAnchorDirective,CommonModule, RouterLink, ArchetypeWheelComponent, ArchetypeIconComponent, ArchetypeDashboardCardComponent],
  templateUrl: './archetype-report-view.component.html',
  styles: [`
    /*
     * Dashboard cards (Dive Deeper section) — 2-up CSS Grid, equal-height rows.
     * Bootstrap 3's float grid leaves taller cards orphaning short ones into
     * staggered rows; grid auto-rows: 1fr fixes that cleanly and gives us
     * consistent gutters without inner-row negative-margin acrobatics.
     */
    .dashboard-card-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      grid-auto-rows: 1fr;
      gap: 20px;
    }
    @media (max-width: 768px) {
      .dashboard-card-grid { grid-template-columns: 1fr; }
    }
    .dashboard-card-grid > app-archetype-dashboard-card { display: flex; }

    /*
     * Six IPSAT Archetypes — same pattern, 3-up on desktop.
     */
    .six-archetypes-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      grid-auto-rows: 1fr;
      gap: 20px;
    }
    @media (max-width: 992px) {
      .six-archetypes-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    }
    @media (max-width: 576px) {
      .six-archetypes-grid { grid-template-columns: 1fr; }
    }
    .six-archetypes-card {
      display: flex;
      flex-direction: column;
      margin-bottom: 0;
      width: 100%;
    }
    .six-archetypes-card .panel-body { flex-grow: 1; }
  `],
})
export class ArchetypeReportViewComponent implements OnInit {
  user: any;
  surveyResultId: string = '';
  archetypeReport: ArchetypeReport | null = null;
  isLoading: boolean = true;
  /** Steve 2026-06-11 item 2: set true when the current SurveyResult's
   *  self-authored statements hash differs from the hash captured on the
   *  saved ArchetypeReport. Drives the "regenerate your report" banner. */
  needsRegeneration: boolean = false;
  archetypeDisplayNames: { [key: string]: string } = ARCHETYPE_DISPLAY_NAMES;
  archetypeDetails: { [key: string]: { tagline: string; icon: string; panelClass: string; description: string } } = ARCHETYPE_META;
  dashboardCards = DASHBOARD_CARDS;
  /** Six-archetype cards toggle which ones have their Learn more expanded inline. */
  expandedArchetypes: Set<string> = new Set();

  toggleArchetypeDetail(key: string): void {
    if (this.expandedArchetypes.has(key)) {
      this.expandedArchetypes.delete(key);
    } else {
      this.expandedArchetypes.add(key);
    }
  }

  constructor(
    private route: ActivatedRoute,
    private archetypeReportService: ArchetypeReportService,
    private userService: UserService,
    private errorHandler: ErrorHandlerService,
    private modalService: BsModalService,
    private authService: AuthService,
    private surveyResultService: SurveyResultService
  ) {}

  /**
   * Steve 2026-06-08 ask: the archetype score-breakdown bar graph is
   * hidden for participants (too confusing on a first read) and shown
   * only to coaches and admin-level roles.
   */
  get canSeeBarGraph(): boolean {
    const payload = this.authService.getTokenPayload();
    const roles: string[] = (payload && payload.roles) || [];
    return roles.some(r => ['coach', 'groupadmin', 'partneradmin', 'admin'].includes(r));
  }

  ngOnInit(): void {
    const params = this.route.snapshot.params;
    const userId = params['user_id'];
    this.surveyResultId = params['survey_result_id'];

    // Load the SurveyResult too — needed to compare its 5 self-authored
    // statements against the hash stored on the ArchetypeReport (Steve
    // 2026-06-11 item 2). If statements have changed, we'll surface the
    // regenerate banner once both come back.
    forkJoin([
      this.userService.getUser(userId),
      this.archetypeReportService.getBySurveyResult(this.surveyResultId),
      this.surveyResultService.getSurveyResult(this.surveyResultId).pipe(catchError(() => of(null))),
    ])
      .pipe(
        tap(([user, report, surveyResult]) => {
          this.user = user;
          this.archetypeReport = report;
          this.isLoading = false;
          if (report && (report as any).identityStatementsHash && surveyResult) {
            this.computeNeedsRegeneration(surveyResult, (report as any).identityStatementsHash);
          }
        }),
        catchError((error) => {
          this.errorHandler.handleError(error);
          this.isLoading = false;
          return of(null);
        })
      )
      .subscribe();
  }

  /**
   * Hash the current SurveyResult's 5 statements and compare against the
   * hash captured at report generation. Same normalization + SHA-256 as
   * the backend (computeStatementsHash in ArchetypeReport.route.js) —
   * keep the two in sync.
   */
  private async computeNeedsRegeneration(surveyResult: any, savedHash: string): Promise<void> {
    const idText = this.extractIdentityStatements(surveyResult);
    const norm = (s: string) => (s || '').trim().replace(/\s+/g, ' ');
    const parts = [
      norm(idText.identity_overview),
      norm(idText.best_contribution),
      norm(idText.developmental_priority),
      norm(idText.potential_derailer),
      norm(idText.vulnerable_setting),
    ];
    const joined = parts.join('\n----\n');
    const encoder = new TextEncoder();
    const data = encoder.encode(joined);
    const digest = await crypto.subtle.digest('SHA-256', data);
    const hex = Array.from(new Uint8Array(digest))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
    this.needsRegeneration = hex !== savedHash;
  }

  private extractIdentityStatements(surveyResult: any): {
    identity_overview: string;
    best_contribution: string;
    developmental_priority: string;
    potential_derailer: string;
    vulnerable_setting: string;
  } {
    const out = {
      identity_overview: '',
      best_contribution: '',
      developmental_priority: '',
      potential_derailer: '',
      vulnerable_setting: '',
    };
    const qs = (surveyResult && surveyResult.questions) || [];
    for (const q of qs) {
      const ans = q?.answer?.name;
      if (typeof ans !== 'string' || ans.trim().length < 30) continue;
      const haystack = `${q.title || ''} ${q.name || ''}`.toLowerCase();
      if (haystack.includes('derailer')) out.potential_derailer = out.potential_derailer || ans;
      else if (haystack.includes('vulnerable') || haystack.includes('setting')) out.vulnerable_setting = out.vulnerable_setting || ans;
      else if (haystack.includes('developmental') || haystack.includes('priority')) out.developmental_priority = out.developmental_priority || ans;
      else if (haystack.includes('contribution')) out.best_contribution = out.best_contribution || ans;
      else if (haystack.includes('identity') || haystack.includes('summary') || haystack.includes('unique combination')) out.identity_overview = out.identity_overview || ans;
    }
    return out;
  }

  openRegenerateModal(): void {
    const modalRef = this.modalService.show(RegenerateArchetypeReportModalComponent, {
      class: 'modal-md',
      initialState: {
        surveyResultId: this.surveyResultId,
      },
    });
    modalRef.content?.onRegenerated.subscribe((report: ArchetypeReport) => {
      this.archetypeReport = report;
      this.needsRegeneration = false;
    });
  }

  /**
   * Convert a display name like 'Bold Initiator' to a key like 'bold_initiator'
   */
  displayNameToKey(displayName: string): Archetype {
    return displayName.toLowerCase().replace(/\s+/g, '_') as Archetype;
  }

  get primaryArchetypeKey(): Archetype | null {
    if (!this.archetypeReport) return null;
    return this.displayNameToKey(this.archetypeReport.archetype.primary);
  }

  get secondaryArchetypeKey(): Archetype | null {
    if (!this.archetypeReport?.archetype.secondary) return null;
    return this.displayNameToKey(this.archetypeReport.archetype.secondary);
  }

  /**
   * Transform the scores map { [displayName: string]: number } into an array for template iteration.
   * The DB stores scores keyed by display name ("Bold Initiator"), so we convert to snake_case keys
   * and sort descending by score.
   */
  get scoresArray(): { archetype: string; displayName: string; score: number }[] {
    if (!this.archetypeReport?.archetype.scores) return [];
    return Object.entries(this.archetypeReport.archetype.scores)
      .map(([key, value]) => ({
        archetype: this.displayNameToKey(key),
        displayName: key,
        score: value,
      }))
      .sort((a, b) => b.score - a.score);
  }

  getPrimaryDetails() {
    return this.archetypeReport?.content?.primary ?? null;
  }

  getOverlayDetails() {
    return this.archetypeReport?.content?.secondary ?? null;
  }

  getScorePercentage(score: number): string {
    return `${Math.round(score)}%`;
  }

  get confidence(): string {
    return (this.archetypeReport?.archetype as any)?.confidence || '';
  }

  get confidenceClass(): string {
    switch (this.confidence) {
      case 'High': return 'label-success';
      case 'Medium': return 'label-warning';
      case 'Low': return 'label-danger';
      default: return 'label-default';
    }
  }

  get confidenceDescription(): string {
    switch (this.confidence) {
      case 'High': return 'Strong signal separation between your primary and other archetypes.';
      case 'Medium': return 'Moderate signal separation. Your primary is clear but other archetypes also resonate.';
      case 'Low': return 'Close scores between top archetypes. Your profile blends multiple patterns.';
      default: return '';
    }
  }

  startsWithVowel(text: string): boolean {
    return /^[aeiou]/i.test(text);
  }

  scrollToFramework(): void {
    document.querySelector('.archetype-framework-section')?.scrollIntoView({ behavior: 'smooth' });
  }

  /**
   * Steve 2026-06-25: parity with the 1.0 IPSAT result page's Share button
   * (`survey-result.component.ts` shareIPSAT()). Reuses the shareable-ipsat
   * modal in mode='archetype' so the public link (#/s/<id>/archetype) renders
   * the 2.0 Archetype Report rather than the 1.0 Identity Profile
   * (Steve 2026-07-07 follow-up).
   */
  openShareModal(): void {
    if (!this.surveyResultId) return;
    const modalRef = this.modalService.show(
      NexleaderShareableIpsatModalComponent,
      {
        initialState: {
          shareableID: this.surveyResultId,
          mode: 'archetype',
        },
      }
    );
    modalRef.content?.close.subscribe(() => modalRef.hide());
  }

  /**
   * Opens the modal for the given dashboard card. Each modal receives the
   * loaded archetype content (primary + optional overlay) from
   * archetypeReport.content. The 4 JSON-driven cards pull from
   * primaryContent; the 2 AI-driven cards (integrated_insights,
   * identity_statements) consume archetypeReport.aiContent when present —
   * those fields are populated at /purchase time in Phase C.
   */
  openCardModal(cardId: DashboardCardId): void {
    if (!this.archetypeReport) return;

    const primaryContent = this.archetypeReport.content?.primary || null;
    const overlayContent = this.archetypeReport.content?.secondary || null;
    const primaryArchetypeName = this.archetypeReport.archetype?.primary || '';
    const userFirstName = (this.user as any)?.firstName;
    const ai = (this.archetypeReport as any).aiContent || {};

    // Per-modal initialState bag — each modal pulls what it needs.
    const initialStateByCard: Record<DashboardCardId, any> = {
      dynamic_operating_pattern: { primaryContent, aiContent: ai },
      integrated_insights: { insights: ai.integrated_insights || [] },
      disclosure_feedback_loop: { primaryContent },
      development_focus: { primaryContent, aiContent: ai },
      archetype_interfaces: { primaryContent, primaryArchetypeName, userFirstName, aiInteractions: ai.archetype_interactions || null },
      identity_statements: { statements: ai.identity_statements || [] },
    };

    const componentMap: Record<DashboardCardId, any> = {
      dynamic_operating_pattern: DynamicOperatingPatternModalComponent,
      integrated_insights: IntegratedInsightsModalComponent,
      disclosure_feedback_loop: DisclosureFeedbackLoopModalComponent,
      development_focus: DevelopmentFocusModalComponent,
      archetype_interfaces: ArchetypeInterfacesModalComponent,
      identity_statements: IdentityStatementsModalComponent,
    };

    const component = componentMap[cardId];
    if (!component) return;

    this.modalService.show(component, {
      class: 'modal-lg',
      initialState: {
        ...initialStateByCard[cardId],
      },
    });
  }
}
